import { useMemo } from "react";
import "./order-totals.scss";

interface OrderTotalsProps {
  rebars: any[];
  nets: any[];
  piles: any[];
}

interface DiameterTotal {
  diameter: number;
  length: number;
  weight: number;
}

const toNumber = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const HOOP_REFERENCE_IDS = new Set<number>([
  8,
  6,
  94,
  95,
  96,
  97,
  98,
  99,
  101,
  102,
  103,
  104,
  105,
  106,
  199,
  204,
  205,
  206,
  207,
  210,
]);

const rebarIsHoop = (item: any): boolean => {
  const catalogShapeId =
    item?.tool?.reference_tool ?? item?.tool?.id;

  return HOOP_REFERENCE_IDS.has(
    toNumber(catalogShapeId)
  );
};

const getItemWeight = (item: any): number => {
  return (
    toNumber(item?.tool?.unit_weight) *
    toNumber(item?.quantity)
  );
};

const getRebarLengthCm = (item: any): number => {
  const sides = item?.tool?.rebar_side || [];

  return sides.reduce(
    (total: number, side: any) =>
      total + toNumber(side?.length),
    0
  );
};

const rebarHasCut = (item: any): boolean => {
  const length = getRebarLengthCm(item);

  return length !== 600 && length !== 1200;
};

const rebarHasBend = (item: any): boolean => {
  const sides = item?.tool?.rebar_side || [];

  return sides.some(
    (side: any) =>
      side?.angle !== null &&
      side?.angle !== undefined &&
      toNumber(side.angle) !== 180
  );
};

const OrderTotals = ({
  rebars = [],
  nets = [],
  piles = [],
}: OrderTotalsProps) => {
  const summary = useMemo(() => {
    const diameterMap = new Map<number, DiameterTotal>();

    const addDiameter = (
      diameterData: any,
      lengthPerUnitMeters: number,
      quantity: number
    ) => {
      if (!diameterData || typeof diameterData !== "object") {
        return;
      }

      const diameter = toNumber(
        diameterData.steel_diameter
      );

      const weightPerMeter = toNumber(
        diameterData.weight
      );

      if (!diameter || !weightPerMeter) {
        return;
      }

      const totalLength =
        lengthPerUnitMeters * quantity;

      const totalWeight =
        totalLength * weightPerMeter;

      const existing = diameterMap.get(diameter) || {
        diameter,
        length: 0,
        weight: 0,
      };

      existing.length += totalLength;
      existing.weight += totalWeight;

      diameterMap.set(diameter, existing);
    };

    rebars.forEach((item: any) => {
      const quantity = toNumber(item?.quantity);

      const lengthMeters =
        getRebarLengthCm(item) / 100;

      addDiameter(
        item?.tool?.diameter,
        lengthMeters,
        quantity
      );
    });

    nets.forEach((item: any) => {
      const tool = item?.tool || {};
      const quantity = toNumber(item?.quantity);

      const xLength = toNumber(tool.x_length);
      const yLength = toNumber(tool.y_length);

      const x1 =
        xLength -
        toNumber(tool.x_2) -
        toNumber(tool.x_3);

      const y1 =
        yLength -
        toNumber(tool.y_2) -
        toNumber(tool.y_3);

      const xGap = toNumber(tool.x_gap);
      const yGap = toNumber(tool.y_gap);

      const numberOfXBars = xGap
        ? Math.round(x1 / xGap + 1)
        : 0;

      const numberOfYBars = yGap
        ? Math.round(y1 / yGap + 1)
        : 0;

      const xBarsLengthMeters =
        (numberOfXBars * yLength) / 100;

      const yBarsLengthMeters =
        (numberOfYBars * xLength) / 100;

      addDiameter(
        tool.x_diameter,
        xBarsLengthMeters,
        quantity
      );

      addDiameter(
        tool.y_diameter,
        yBarsLengthMeters,
        quantity
      );
    });

    piles.forEach((item: any) => {
      const tool = item?.tool || {};
      const quantity = toNumber(item?.quantity);

      const totalLongitudinalLength =
        (
          toNumber(tool.l1_length) +
          toNumber(tool.l2_length) +
          toNumber(tool.l3_length)
        ) / 100;

      const earLength =
        toNumber(tool.ear_length) / 100;

      const addLongitudinalBars = (
        diameterData: any,
        amount: unknown
      ) => {
        const barsAmount = toNumber(amount);

        if (!barsAmount) {
          return;
        }

        const lengthPerPile =
          (totalLongitudinalLength + earLength) *
          barsAmount;

        addDiameter(
          diameterData,
          lengthPerPile,
          quantity
        );
      };

      addLongitudinalBars(
        tool.base_diameter,
        tool.base_amount
      );

      addLongitudinalBars(
        tool.external_diameter,
        tool.external_amount
      );

      addLongitudinalBars(
        tool.middle_diameter,
        tool.middle_amount
      );

      addLongitudinalBars(
        tool.internal_diameter,
        tool.internal_amount
      );

      const oneHoopLengthCm =
        toNumber(tool.cage_diameter) * 3.14;

      const numberOfL2Hoops =
        toNumber(tool.l2_gap) > 0
          ? toNumber(tool.l2_length) /
            toNumber(tool.l2_gap)
          : 0;

      const numberOfL3Hoops =
        toNumber(tool.l3_gap) > 0
          ? toNumber(tool.l3_length) /
            toNumber(tool.l3_gap)
          : 0;

      const spiralLengthMeters =
        (
          (numberOfL2Hoops + numberOfL3Hoops) *
          oneHoopLengthCm
        ) / 100;

      addDiameter(
        tool.spiral_wire_diameter,
        spiralLengthMeters,
        quantity
      );

      const ringLengthMeters =
        (
          toNumber(tool.ring_quantity) *
          oneHoopLengthCm
        ) / 100;

      addDiameter(
        tool.ring_diameter,
        ringLengthMeters,
        quantity
      );
    });

    const diameters = Array.from(
      diameterMap.values()
    ).sort((a, b) => a.diameter - b.diameter);

    const diameterLengthTotal = diameters.reduce(
      (total, item) => total + item.length,
      0
    );

    const diameterWeightTotal = diameters.reduce(
      (total, item) => total + item.weight,
      0
    );

    const rebarUnits = rebars.reduce(
      (total: number, item: any) =>
        total + toNumber(item?.quantity),
      0
    );

    const rebarWeight = rebars.reduce(
      (total: number, item: any) =>
        total + getItemWeight(item),
      0
    );

    const standardNets = nets.filter(
      (item: any) => item?.tool?.standard_net
    );

    const standardNetUnits = standardNets.reduce(
      (total: number, item: any) =>
        total + toNumber(item?.quantity),
      0
    );

    const standardNetWeight = standardNets.reduce(
      (total: number, item: any) =>
        total + getItemWeight(item),
      0
    );

    const pileUnits = piles.reduce(
      (total: number, item: any) =>
        total + toNumber(item?.quantity),
      0
    );

    const pileWeight = piles.reduce(
      (total: number, item: any) =>
        total + getItemWeight(item),
      0
    );

    const orderUnits =
      rebarUnits +
      standardNetUnits +
      pileUnits;

    const orderWeight =
      rebarWeight +
      standardNetWeight +
      pileWeight;

    const cuttingWeight = rebars
      .filter(rebarHasCut)
      .reduce(
        (total: number, item: any) =>
          total + getItemWeight(item),
        0
      );

    const bendingWeight = rebars
      .filter(rebarHasBend)
      .reduce(
        (total: number, item: any) =>
          total + getItemWeight(item),
        0
      );

      const hoopUnits = rebars
  .filter(rebarIsHoop)
  .reduce(
    (total: number, item: any) =>
      total + toNumber(item?.quantity),
    0
  );

    const wasteTons =
      (orderWeight / 1000) * 0.03;

    const totalWithWasteTons =
      orderWeight / 1000 + wasteTons;

    return {
      diameters,
      diameterLengthTotal,
      diameterWeightTotal,
      rebarUnits,
      rebarWeight,
      standardNetUnits,
      standardNetWeight,
      pileUnits,
      pileWeight,
      orderUnits,
      orderWeight,
      cuttingWeight,
      bendingWeight,
      hoopUnits,
      wasteTons,
      totalWithWasteTons,
    };
  }, [rebars, nets, piles]);

  const formatNumber = (
    value: number,
    maximumFractionDigits = 2
  ) =>
    value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    });

  const formatTons = (weightKg: number) =>
    formatNumber(weightKg / 1000, 2);

  return (
    <div className="order-totals" dir="rtl">
      <div className="order-total-card processing-card">
        <div className="order-total-card__title">
          עיבוד ופחת
        </div>

        <table>
          <tbody>
            <tr>
              <th>חיתוך (ק״ג)</th>
              <td>
                {formatNumber(
                  summary.cuttingWeight,
                  1
                )}
              </td>
            </tr>

            <tr>
              <th>כיפוף (ק״ג)</th>
              <td>
                {formatNumber(
                  summary.bendingWeight,
                  1
                )}
              </td>
            </tr>

            <tr>
            <th>יחידות</th>
            <td>
              {formatNumber(summary.hoopUnits, 0)}
            </td>
          </tr>

            {/* <tr>
              <th>יחידות</th>
              <td>
                {formatNumber(summary.orderUnits, 0)}
              </td>
            </tr> */}

            <tr>
              <th>פחת 3% (טון)</th>
              <td>
                {summary.wasteTons.toFixed(4)}
              </td>
            </tr>

            <tr>
              <th>סה״כ כולל פחת (טון)</th>
              <td>
                {summary.totalWithWasteTons.toFixed(4)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="order-total-card diameters-card">
        <div className="order-total-card__title">
          קטרים
        </div>

        <table>
          <tbody>
            {summary.diameters.map((item) => (
              <tr key={item.diameter}>
                <th>{item.diameter}</th>

                <td>
                  {formatNumber(item.length, 2)}
                </td>

                <td>
                  {formatNumber(item.weight, 2)}
                </td>
              </tr>
            ))}

            <tr className="total-row">
              <th>סה״כ</th>

              <td>
                {formatNumber(
                  summary.diameterLengthTotal,
                  2
                )}
              </td>

              <td>
                {formatNumber(
                  summary.diameterWeightTotal,
                  2
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="table-units table-units--three">
          <span></span>
          <span>אורך (מטר)</span>
          <span>משקל (ק״ג)</span>
        </div>
      </div>

      <div className="order-total-card order-card">
        <div className="order-total-card__title">
          הזמנה
        </div>

        <table>
          <tbody>
            <tr>
              <th>מוטות</th>
              <td>{summary.rebarUnits}</td>
              <td>{formatTons(summary.rebarWeight)}</td>
            </tr>

            <tr>
              <th>רשתות סטנדרטיות</th>
              <td>{summary.standardNetUnits}</td>
              <td>
                {formatTons(summary.standardNetWeight)}
              </td>
            </tr>

            <tr>
              <th>כלונסאות</th>
              <td>{summary.pileUnits}</td>
              <td>{formatTons(summary.pileWeight)}</td>
            </tr>

            <tr className="total-row">
              <th>סה״כ</th>
              <td>{summary.orderUnits}</td>
              <td>{formatTons(summary.orderWeight)}</td>
            </tr>
          </tbody>
        </table>

        <div className="table-units table-units--three">
          <span></span>
          <span>יחידות</span>
          <span>משקל (טון)</span>
        </div>
      </div>
    </div>
  );
};

export default OrderTotals;